import React, { useCallback, useEffect, useRef } from 'react';

function scrollActiveTabIntoView(scrollContainer, activeTabId) {
  if (!scrollContainer || !activeTabId) return;

  const activeEl = scrollContainer.querySelector(`[data-tab-id="${activeTabId}"]`);
  if (!activeEl) return;

  const tabLeft = activeEl.offsetLeft;
  const tabWidth = activeEl.offsetWidth;
  const containerWidth = scrollContainer.clientWidth;
  const maxScroll = scrollContainer.scrollWidth - containerWidth;
  const targetScroll = tabLeft - (containerWidth - tabWidth) / 2;

  scrollContainer.scrollTo({
    left: Math.max(0, Math.min(targetScroll, maxScroll)),
    behavior: 'smooth',
  });
}

const TabNavigation = React.forwardRef(function TabNavigation(
  { tabs, activeTab, onTabChange },
  ref
) {
  const scrollContainerRef = useRef(null);

  const handleTabClick = useCallback(
    (tabId) => () => {
      onTabChange(tabId);
    },
    [onTabChange]
  );

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return undefined;

    const runScroll = () => scrollActiveTabIntoView(scrollContainer, activeTab);

    runScroll();
    const frame = requestAnimationFrame(runScroll);
    const afterLayout = setTimeout(runScroll, 50);
    const afterEnter = setTimeout(runScroll, 320);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(afterLayout);
      clearTimeout(afterEnter);
    };
  }, [activeTab, tabs]);

  return (
    <div ref={ref} className="px-4 sm:px-6 py-2 sm:py-3 border-b border-gray-200">
      <div
        ref={scrollContainerRef}
        className="flex space-x-2 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {tabs.map((tabItem) => (
          <button
            key={tabItem.id}
            type="button"
            data-tab-id={tabItem.id}
            onClick={handleTabClick(tabItem.id)}
            className={`px-3 sm:px-4 py-2 rounded-md font-medium text-xs sm:text-sm transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
              activeTab === tabItem.id
                ? 'bg-vervestacks-primary text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {tabItem.name}
          </button>
        ))}
      </div>
    </div>
  );
});

export default React.memo(TabNavigation);
